<template>
  <div>
    <!-- Add Category Dialog -->
    <v-dialog v-model="addOpenModel" max-width="560px" persistent>
      <v-card style="border-radius: 16px !important; overflow: hidden;">
        <div style="background: linear-gradient(180deg, #ffffff 0%, #eef4ff 100%); display: flex; justify-content: space-between; align-items: center; padding: 14px 12px 14px 24px;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <v-icon color="#1976D2" size="22">mdi-tag-multiple-outline</v-icon>
            <span style="font-size: 18px; font-weight: 600; color: #1976D2;">{{ $t('manageCategories') }}</span>
          </div>
          <v-btn icon variant="text" size="small" @click="$emit('close-add')">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text style="padding: 24px 24px 16px 24px;">
          <v-combobox
            v-model="newCategoryNameModel"
            :items="categoryNameOptions"
            :label="$t('searchOrAddCategory')"
            variant="outlined"
            density="comfortable"
            :rules="[categoryNameRequiredRule]"
            :no-data-text="$t('typeNewTagToAdd')"
            :menu-props="{ maxHeight: 260 }"
            prepend-inner-icon="mdi-tag-search-outline"
            autofocus
            clearable
            @keydown.enter.prevent="$emit('save')"
            hide-details="auto"
            style="border-radius: 12px;"
          />

          <div class="category-manager-summary">
            <span class="category-manager-label">{{ $t('existingCategories') }}</span>
            <v-chip size="small" color="#E3F2FD" text-color="#1565C0">{{ filteredCategories.length }}</v-chip>
          </div>

          <div class="category-manager-list">
            <div v-if="filteredCategories.length === 0" class="category-manager-empty">
              <v-icon size="18" color="#90A4AE">mdi-tag-off-outline</v-icon>
              <span>{{ $t('noCategoriesFound') }}</span>
            </div>
            <div
              v-for="category in filteredCategories"
              :key="category.id || category.name"
              class="category-manager-item"
            >
              <div class="category-manager-name">
                <v-icon size="16" color="#1976D2">mdi-tag-outline</v-icon>
                <span>{{ category.name }}</span>
              </div>
              <div class="category-manager-actions">
                <v-btn icon variant="text" size="small" @click="$emit('open-rename', category)">
                  <v-icon size="18" color="#1976D2">mdi-pencil-outline</v-icon>
                </v-btn>
                <v-btn icon variant="text" size="small" @click="$emit('delete', category)">
                  <v-icon size="18" color="#D32F2F">mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions style="padding: 0 24px 16px 24px; display: flex; justify-content: flex-end; gap: 10px;">
          <v-btn class="delete-cancel-btn" @click="$emit('close-add')">{{ $t('cancel') }}</v-btn>
          <v-btn
            style="background: linear-gradient(90deg, #1976D2 0%, #42A5F5 100%) !important; color: white !important; border-radius: 11px !important; height: 40px !important; min-width: 100px !important; text-transform: none !important; font-weight: 500 !important; box-shadow: 0 2px 8px rgba(25,118,210,0.25) !important;"
            :disabled="!normalizeCategoryDisplayName(newCategoryName)"
            @click="$emit('save')"
          >
            <v-icon left size="18" style="margin-right: 4px;">mdi-plus-circle-outline</v-icon>
            {{ $t('add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="renameOpenModel" max-width="420px" persistent>
      <v-card style="border-radius: 16px !important; overflow: hidden;">
        <div style="background: linear-gradient(180deg, #ffffff 0%, #eef4ff 100%); display: flex; justify-content: space-between; align-items: center; padding: 14px 12px 14px 24px;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <v-icon color="#1976D2" size="22">mdi-tag-edit-outline</v-icon>
            <span style="font-size: 18px; font-weight: 600; color: #1976D2;">{{ $t('renameCategory') }}</span>
          </div>
          <v-btn icon variant="text" size="small" @click="$emit('close-rename')">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text style="padding: 24px 24px 16px 24px;">
          <v-text-field
            v-model="renameCategoryNameModel"
            :label="$t('newCategoryNameLabel')"
            variant="outlined"
            density="comfortable"
            :rules="[categoryNameRequiredRule]"
            autofocus
            @keydown.enter.prevent="$emit('rename')"
            hide-details="auto"
            style="border-radius: 12px;"
          />
        </v-card-text>
        <v-card-actions style="padding: 0 24px 16px 24px; display: flex; justify-content: flex-end; gap: 10px;">
          <v-btn class="delete-cancel-btn" @click="$emit('close-rename')">{{ $t('cancel') }}</v-btn>
          <v-btn
            style="background: linear-gradient(90deg, #1976D2 0%, #42A5F5 100%) !important; color: white !important; border-radius: 11px !important; height: 40px !important; min-width: 100px !important; text-transform: none !important; font-weight: 500 !important; box-shadow: 0 2px 8px rgba(25,118,210,0.25) !important;"
            :disabled="!normalizeCategoryDisplayName(renameCategoryName)"
            @click="$emit('rename')"
          >
            <v-icon left size="18" style="margin-right: 4px;">mdi-content-save-edit-outline</v-icon>
            {{ $t('rename') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import categoryOptionsMixin from '@/mixins/categoryOptionsMixin.js';

// Shared Manage-Categories + Rename-Category dialogs, extracted from the
// byte-duplicated markup in components/ModelManagement.vue and
// views/GPTModelManagement.vue. All category CRUD logic stays in the parents;
// this component only renders and forwards events.
export default {
  name: 'CategoryManageDialogs',
  mixins: [categoryOptionsMixin],
  props: {
    addOpen: { type: Boolean, default: false },
    renameOpen: { type: Boolean, default: false },
    newCategoryName: { type: [String, Object], default: '' },
    renameCategoryName: { type: String, default: '' },
    categoryNameOptions: { type: Array, default: () => [] },
    filteredCategories: { type: Array, default: () => [] },
  },
  emits: [
    'update:addOpen',
    'update:renameOpen',
    'update:newCategoryName',
    'update:renameCategoryName',
    'save',
    'rename',
    'delete',
    'open-rename',
    'close-add',
    'close-rename',
  ],
  computed: {
    addOpenModel: {
      get() {
        return this.addOpen;
      },
      set(value) {
        this.$emit('update:addOpen', value);
      },
    },
    renameOpenModel: {
      get() {
        return this.renameOpen;
      },
      set(value) {
        this.$emit('update:renameOpen', value);
      },
    },
    newCategoryNameModel: {
      get() {
        return this.newCategoryName;
      },
      set(value) {
        this.$emit('update:newCategoryName', value);
      },
    },
    renameCategoryNameModel: {
      get() {
        return this.renameCategoryName;
      },
      set(value) {
        this.$emit('update:renameCategoryName', value);
      },
    },
  },
};
</script>

<style scoped>
.category-manager-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
  margin-bottom: 12px;
}

.category-manager-label {
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
}

.category-manager-list {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid rgba(25, 118, 210, 0.12);
  border-radius: 12px;
  background: #fafcff;
}

.category-manager-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.category-manager-item:last-child {
  border-bottom: none;
}

.category-manager-name {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: #1f2937;
  font-size: 13px;
}

.category-manager-name span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-manager-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.category-manager-empty {
  min-height: 96px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #90a4ae;
  font-size: 13px;
}
</style>
